import api from "./api";

export const testService = {
	// Get all tests
	getTests: async (params = {}) => {
		const response = await api.get("/tests", { params });
		return response.data;
	},

	// Get single test
	getTest: async (id) => {
		const response = await api.get(`/tests/${id}`);
		return response.data;
	},

	// Create test
	createTest: async (testData) => {
		const response = await api.post("/tests", testData);
		return response.data;
	},

	// Update test
	updateTest: async (id, testData) => {
		const response = await api.put(`/tests/${id}`, testData);
		return response.data;
	},

	// Delete test
	deleteTest: async (id) => {
		await api.delete(`/tests/${id}`);
	},

	// Toggle favorite
	toggleFavorite: async (id) => {
		const response = await api.post(`/tests/${id}/favorite`);
		return response.data;
	},

	// Search tests
	searchTests: async (query) => {
		const response = await api.get("/tests/search", {
			params: { q: query },
		});
		return response.data;
	},

	// Execute test. The backend checks the caller may run it and hands it to
	// the executor; the browser never calls the executor's HTTP API. stepIds
	// (API tests) runs only those saved requests.
	executeTest: async (id, stepIds) => {
		const test = await testService.getTest(id);

		// If test has data binding, use data-driven execution
		if (test.dataBinding?.dataSetId) {
			return testService.executeTestWithData(
				id,
				test.dataBinding.dataSetId,
				test.dataBinding.mode || "ITERATE_ALL",
				test.dataBinding.specificRowIndex,
			);
		}

		const response = await api.post("/executions/run", { testId: test.id, stepIds });
		return { ...response.data, executionId: response.data.id };
	},

	// Run in Loop: the backend runs the test `count` times as one grouped run
	// (a suite run without a suite, listed on Suite Runs) and returns it.
	runLoop: async (testId, count, parallel) => {
		const response = await api.post("/executions/loop", { testId, count, parallel });
		return response.data;
	},

	// Poll a suite execution until it reaches a terminal status
	pollSuiteExecutionUntilComplete: async (suiteExecutionId, intervalMs = 3000, maxMs = 600000) => {
		const terminal = new Set(["COMPLETED", "FAILED", "CANCELLED"]);
		for (let elapsed = 0; elapsed < maxMs; elapsed += intervalMs) {
			const res = await api.get(`/suites/executions/${suiteExecutionId}`);
			if (terminal.has((res.data.status || "").toUpperCase())) return res.data;
			await new Promise((r) => setTimeout(r, intervalMs));
		}
		throw new Error("Suite execution timed out after 10 minutes");
	},

	// Execute test with data-driven parameters
	executeTestWithData: async (testId, dataSetId, mode, rowIndex) => {
		const response = await api.post("/executions/data-driven", {
			testId,
			dataSetId,
			mode: mode || "ITERATE_ALL",
			rowIndex: mode === "SINGLE_ROW" ? rowIndex : null,
		});

		const executions = response.data;
		if (!executions || executions.length === 0) {
			throw new Error("No executions created");
		}

		// Return the first execution's ID for navigation
		return {
			executionId: executions[0].id,
			status: "started",
			message: `Data-driven execution started (${executions.length} run${executions.length > 1 ? "s" : ""})`,
			executions,
		};
	},
};

export default testService;
